/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  Share, Alert, Dimensions, Modal, TextInput, ActivityIndicator, Image,
  Animated, KeyboardAvoidingView, Platform, Linking,
} from 'react-native';
import { Ionicons, FontAwesome5, MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; } catch {
  LinearGradient = ({colors,style,children,...p}) => <View style={[style,{backgroundColor:colors?.[0]||'#667eea'}]} {...p}>{children}</View>;
}

const { width } = Dimensions.get('window');

// ─── Shared keys ─────────────────────────────────────────────────────────────
const SHARED = {
  PORTAL_APPS : 'intern_portal_applications',
  PROGRAMS    : 'intern_programs',
  SAVED       : 'studentData',
};
const mkId = () => `app_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
const loadStudentProfile = async () => {
  try { const r=await AsyncStorage.getItem(SHARED.SAVED); if(r){const d=JSON.parse(r);return d.profile||{};} } catch {} return {};
};
const loadStudentApps = async () => {
  try { const r=await AsyncStorage.getItem(SHARED.PORTAL_APPS); if(r) return JSON.parse(r)||[]; } catch {} return [];
};
const submitToPortal = async (newApp) => {
  try {
    const existing = await loadStudentApps();
    const merged   = [newApp, ...existing.filter(a=>a.id!==newApp.id)];
    await AsyncStorage.setItem(SHARED.PORTAL_APPS, JSON.stringify(merged));
    const sdRaw = await AsyncStorage.getItem(SHARED.SAVED); const sd=sdRaw?JSON.parse(sdRaw):{};
    sd.applications = merged; await AsyncStorage.setItem(SHARED.SAVED, JSON.stringify(sd));
    return true;
  } catch { return false; }
};
const toggleSaveToPortal = async (id, saving) => {
  try {
    const sdRaw=await AsyncStorage.getItem(SHARED.SAVED); const sd=sdRaw?JSON.parse(sdRaw):{};
    const saved=sd.savedPrograms||[];
    sd.savedPrograms=saving?[...new Set([...saved,id])]:saved.filter(x=>x!==id);
    await AsyncStorage.setItem(SHARED.SAVED,JSON.stringify(sd));
  } catch {}
};
const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); } catch { return d||''; } };
const daysLeft = d => Math.ceil((new Date(d)-new Date())/(1000*60*60*24));

const DEFAULT = {
  id:'1', title:'Civil Engineering Virtual Experience', company:'Arup Group',
  location:'London, UK', duration:'12-16 hours', difficulty:'Intermediate',
  category:'Engineering', compensation:'Competitive',
  deadline:new Date(Date.now()+30*86400000).toISOString().split('T')[0],
  description:'Design and analyze structural systems for real-world infrastructure projects. Work on bridge design, building information modeling, and sustainability assessment while learning from industry experts.',
  modules:[
    {id:'1a',title:'Structural Analysis Fundamentals',status:'completed',duration:'3 hours'},
    {id:'1b',title:'Bridge Design Project',status:'in-progress',duration:'4 hours'},
    {id:'1c',title:'Building Information Modeling',status:'not-started',duration:'3 hours'},
    {id:'1d',title:'Sustainability Assessment',status:'not-started',duration:'3 hours'},
    {id:'1e',title:'Final Project Presentation',status:'not-started',duration:'3 hours'},
  ],
  skills:['AutoCAD','Structural Analysis','BIM','Sustainability','Project Management','Technical Drawing'],
  benefits:['Certificate','Portfolio Project','Industry Mentorship','LinkedIn Recommendation'],
  rating:4.8, participants:2340, matchScore:85,
  color:'#f59e0b', icon:'bridge',
  requirements:['Basic understanding of engineering principles','Interest in civil engineering and infrastructure','Access to computer with internet','Commitment to complete all modules'],
  whatYouWillLearn:['Structural analysis techniques and methodologies','Bridge design principles and best practices','Building Information Modeling (BIM) software usage','Sustainability assessment in construction projects'],
  applicationProcess:['Complete online application form','Upload CV and motivation letter','Skills assessment','Video interview','Offer'],
  startDate:'Rolling intake', format:'Online self-paced', certification:'Certificate of completion', mentorship:'Access to industry mentors',
  aboutCompany:'Arup is a global engineering firm known for iconic structures like the Sydney Opera House. They pioneer sustainable design across infrastructure, transport, and buildings.',
};
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ─────────────────────────────────────────────────────────────────────────────
export default function InternshipDetailsScreen({ navigation: navProp, route }) {
  const navHook     = useNavigation();
  const navigation  = navProp || navHook;
  const { internship } = route?.params || {};

  // Merge incoming internship with defaults
  const d = {
    ...DEFAULT,
    ...(internship || {}),
    modules:           internship?.modules           || DEFAULT.modules,
    skills:            internship?.skills            || DEFAULT.skills,
    benefits:          internship?.benefits          || DEFAULT.benefits,
    requirements:      internship?.requirements      || DEFAULT.requirements,
    whatYouWillLearn:  internship?.whatYouWillLearn  || DEFAULT.whatYouWillLearn,
    applicationProcess:internship?.applicationProcess|| DEFAULT.applicationProcess,
  };

  // ── State ──────────────────────────────────────────────────────────────────
  const [isSaved,        setIsSaved]        = useState(false);
  const [isApplied,      setIsApplied]      = useState(false);
  const [applying,       setApplying]       = useState(false);
  const [activeTab,      setActiveTab]      = useState('overview');
  const [studentProfile, setStudentProfile] = useState({});
  const [showAppForm,    setShowAppForm]    = useState(false);
  const [appForm, setAppForm] = useState({ coverLetter:'', whyInterested:'', relevantExperience:'', availability:'' });
  const [moduleProgress, setModuleProgress] = useState({});
  const [showReviewModal,setShowReviewModal]= useState(false);
  const [review, setReview] = useState({ rating:0, comment:'' });
  const [reviews, setReviews] = useState([]);
  // Fetch real reviews for this internship from /api/data/internship-reviews
  // Assumes columns: id, internship_id, name, rating, comment, date
  useEffect(() => {
    if (!d?.id) return;
    let mounted = true;
    (async () => {
      try {
        const token = (await AsyncStorage.getItem('auth_token'))
                  || (await AsyncStorage.getItem('userToken'))
                  || (await AsyncStorage.getItem('token')) || '';
        const res = await fetch(
          `https://api.jobeng.ai/api/data/internship-reviews?internship_id=${encodeURIComponent(d.id)}&limit=50`,
          {
            headers: {
              'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json.data || json.results || []);
        if (mounted) setReviews(list);
      } catch (e) { console.warn('[InternshipDetailsScreen] reviews fetch failed:', e.message); }
    })();
    return () => { mounted = false; };
  }, [d?.id]);

  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const scrollRef = useRef(null);

  useEffect(() => {
    Animated.timing(fadeAnim,{toValue:1,duration:500,useNativeDriver:true}).start();
    loadSyncState();
    loadModuleProgress();
  }, []);

  useFocusEffect(useCallback(() => { loadSyncState(); }, []));

  const loadSyncState = async () => {
    const profile = await loadStudentProfile();
    setStudentProfile(profile);
    const apps = await loadStudentApps();
    setIsApplied(apps.some(a=>a.programId===d.id));
    const sdRaw = await AsyncStorage.getItem(SHARED.SAVED);
    if (sdRaw) { const sd=JSON.parse(sdRaw); setIsSaved((sd.savedPrograms||[]).includes(d.id)); }
  };

  const loadModuleProgress = async () => {
    try {
      const raw = await AsyncStorage.getItem(`module_progress_${d.id}`);
      if (raw) setModuleProgress(JSON.parse(raw));
    } catch {}
  };

  const saveModuleProgress = async (updated) => {
    try { await AsyncStorage.setItem(`module_progress_${d.id}`, JSON.stringify(updated)); } catch {}
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const nowSaved = !isSaved;
    setIsSaved(nowSaved);
    await toggleSaveToPortal(d.id, nowSaved);
    Alert.alert(nowSaved?'Saved 🔖':'Removed', nowSaved?'Added to your saved list in the Student Portal.':'Removed from saved list.');
  };

  const handleShare = async () => {
    try { await Share.share({ title:d.title, message:`${d.title} at ${d.company}\n${d.duration} · ${d.location}\nRating: ${d.rating}/5 ⭐\nDeadline: ${fmtDate(d.deadline)}\n\nApply via JOBENG Student Portal!` }); } catch {}
  };

  const handleApply = () => {
    if (isApplied) { Alert.alert('Already Applied','Check your Student Portal for status updates.'); return; }
    setShowAppForm(true);
  };

  const submitApplication = async () => {
    if (!appForm.coverLetter.trim()||!appForm.availability.trim()) {
      Alert.alert('Required Fields','Cover Letter and Availability are required.'); return;
    }
    if (d.applicationLink) {
      Alert.alert('External Application','Also open the company website?',[
        {text:'Portal Only',onPress:()=>doSubmit()},
        {text:'Both',onPress:()=>{Linking.openURL(d.applicationLink).catch(()=>{}); doSubmit();}},
        {text:'Cancel',style:'cancel'},
      ]); return;
    }
    await doSubmit();
  };

  const doSubmit = async () => {
    setApplying(true);
    const newApp = {
      id:mkId(), programId:d.id, programTitle:d.title, company:d.company,
      status:'Submitted', appliedDate:new Date().toISOString().split('T')[0],
      submittedAt:new Date().toISOString(), progress:10, nextStep:'Initial Review',
      feedback:'Application submitted successfully.', documents:['Resume'], source:'details_screen',
      ...appForm,
      studentName:studentProfile.name||'', studentEmail:studentProfile.email||'',
      studentPhone:studentProfile.phone||'', studentUniversity:studentProfile.university||'',
      studentMajor:studentProfile.major||'', studentGpa:studentProfile.gpa||'',
      studentSkills:studentProfile.skills||[],
    };
    const ok = await submitToPortal(newApp);
    setApplying(false);
    if (ok) {
      setIsApplied(true); setShowAppForm(false);
      setAppForm({coverLetter:'',whyInterested:'',relevantExperience:'',availability:''});
      Alert.alert('🎉 Applied!',`Your application to ${d.title} at ${d.company} has been submitted and is visible in your Student Portal.`);
    } else { Alert.alert('Error','Could not submit. Please try again.'); }
  };

  const toggleModule = async (moduleId) => {
    const updated = { ...moduleProgress, [moduleId]: !moduleProgress[moduleId] };
    setModuleProgress(updated);
    await saveModuleProgress(updated);
  };

  const submitReview = () => {
    if (!review.comment.trim()||review.rating===0) { Alert.alert('Required','Please add a rating and comment.'); return; }
    const newReview = { name:studentProfile.name||'Student', rating:review.rating, comment:review.comment, date:new Date().toISOString().split('T')[0] };
    setReviews(prev=>[newReview,...prev]);
    setReview({rating:0,comment:''});
    setShowReviewModal(false);
    Alert.alert('Thanks!','Your review has been submitted.');
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const completedModules = d.modules.filter(m => moduleProgress[m.id] || m.status==='completed').length;
  const progressPct      = d.modules.length > 0 ? Math.round((completedModules/d.modules.length)*100) : 0;
  const days             = daysLeft(d.deadline);
  const isPast           = days < 0;
  const urgent           = days >= 0 && days <= 7;
  const accentColor      = d.color || '#667eea';
  const avgRating        = reviews.length > 0 ? (reviews.reduce((s,r)=>s+r.rating,0)/reviews.length).toFixed(1) : d.rating;

  const renderStars = (n) => [1,2,3,4,5].map(s=>(
    <MaterialCommunityIcons key={s} name={s<=Math.round(n)?'star':'star-outline'} size={16} color="#F59E0B"/>
  ));

  const TABS = [
    {id:'overview',    title:'Overview',  icon:'information-outline'},
    {id:'modules',     title:'Modules',   icon:'book-open-variant'},
    {id:'skills',      title:'Skills',    icon:'star-outline'},
    {id:'application', title:'Apply',     icon:'send'},
    {id:'reviews',     title:'Reviews',   icon:'comment-outline'},
  ];

  // ── Tab renders ───────────────────────────────────────────────────────────
  const renderOverview = () => (
    <View>
      <Text style={st.desc}>{d.description}</Text>

      {/* Quick info cards */}
      <View style={st.infoGrid}>
        {[
          {icon:'clock-outline',label:'Duration',value:d.duration},
          {icon:'signal',label:'Level',value:d.difficulty||'All Levels'},
          {icon:'map-marker-outline',label:'Location',value:d.location},
          {icon:'currency-gbp',label:'Compensation',value:d.compensation||'Competitive'},
          {icon:'account-group',label:'Participants',value:(d.participants||0).toLocaleString()},
          {icon:'calendar-outline',label:'Deadline',value:fmtDate(d.deadline)},
        ].map((item,i)=>(
          <View key={i} style={[st.infoCard,{borderColor:urgent&&item.label==='Deadline'?'#EF4444':'#E5E7EB'}]}>
            <MaterialCommunityIcons name={item.icon} size={20} color={accentColor}/>
            <Text style={st.infoLabel}>{item.label}</Text>
            <Text style={[st.infoValue,urgent&&item.label==='Deadline'&&{color:'#EF4444',fontWeight:'700'}]}>{item.value}</Text>
          </View>
        ))}
      </View>

      {/* What you'll learn */}
      <Text style={st.sectionTitle}>What You'll Learn</Text>
      {d.whatYouWillLearn.map((item,i)=>(
        <View key={i} style={st.bulletRow}>
          <View style={[st.bulletDot,{backgroundColor:accentColor}]}/>
          <Text style={st.bulletT}>{item}</Text>
        </View>
      ))}

      {/* Requirements */}
      <Text style={st.sectionTitle}>Requirements</Text>
      {d.requirements.map((req,i)=>(
        <View key={i} style={st.bulletRow}>
          <MaterialCommunityIcons name="check-circle-outline" size={15} color="#10B981" style={{marginTop:1}}/>
          <Text style={st.bulletT}>{req}</Text>
        </View>
      ))}

      {/* Benefits */}
      <Text style={st.sectionTitle}>Benefits & Perks</Text>
      <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:16}}>
        {d.benefits.map((b,i)=>(
          <View key={i} style={[st.benefitChip,{backgroundColor:accentColor+'18',borderColor:accentColor+'33'}]}>
            <MaterialCommunityIcons name="check" size={13} color={accentColor}/>
            <Text style={[st.benefitChipT,{color:accentColor}]}>{b}</Text>
          </View>
        ))}
      </View>

      {/* About company */}
      <Text style={st.sectionTitle}>About {d.company}</Text>
      <Text style={st.desc}>{d.aboutCompany||'Company information not available.'}</Text>

      {/* Important info */}
      <View style={[st.importantCard,{borderLeftColor:accentColor}]}>
        <MaterialCommunityIcons name="information" size={20} color={accentColor}/>
        <View style={{flex:1}}>
          <Text style={{fontSize:13,fontWeight:'700',color:'#1F2937',marginBottom:6}}>Programme Information</Text>
          {[['Start Date',d.startDate||'Rolling intake'],['Format',d.format||'Online'],['Certification',d.certification||'Certificate available'],['Mentorship',d.mentorship||'Mentorship included']].map(([k,v])=>(
            <View key={k} style={{flexDirection:'row',gap:8,marginBottom:3}}>
              <Text style={{fontSize:12,color:'#64748B',width:90}}>{k}</Text>
              <Text style={{flex:1,fontSize:12,color:'#374151',fontWeight:'500'}}>{v}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  const renderModules = () => (
    <View>
      {/* Progress bar */}
      <View style={st.progCard}>
        <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:8}}>
          <Text style={{fontSize:14,fontWeight:'700',color:'#1F2937'}}>Your Progress</Text>
          <Text style={[{fontSize:15,fontWeight:'900'},progressPct===100?{color:'#10B981'}:{color:accentColor}]}>{progressPct}%</Text>
        </View>
        <View style={{height:10,backgroundColor:'#E5E7EB',borderRadius:5,overflow:'hidden'}}>
          <View style={{height:10,borderRadius:5,backgroundColor:progressPct===100?'#10B981':accentColor,width:`${progressPct}%`}}/>
        </View>
        <Text style={{fontSize:12,color:'#64748B',marginTop:6}}>{completedModules} of {d.modules.length} modules completed</Text>
      </View>

      {d.modules.map((mod,i)=>{
        const done = moduleProgress[mod.id] || mod.status==='completed';
        const inProg = !done && mod.status==='in-progress';
        return (
          <TouchableOpacity key={mod.id||i} style={st.modCard} onPress={()=>toggleModule(mod.id||String(i))}>
            <View style={[st.modIcon,{backgroundColor:done?'#10B981':inProg?accentColor:'#E5E7EB'}]}>
              <MaterialCommunityIcons name={done?'check':inProg?'play':'lock'} size={16} color="#FFF"/>
            </View>
            <View style={{flex:1}}>
              <Text style={[st.modTitle,done&&{textDecorationLine:'line-through',color:'#94A3B8'}]}>Module {i+1}: {mod.title||'Module'}</Text>
              <Text style={st.modDur}>{mod.duration||'Duration TBD'}</Text>
            </View>
            <View style={[st.modBadge,{backgroundColor:done?'#DCFCE7':inProg?'#FEF3C7':'#F3F4F6'}]}>
              <Text style={[st.modBadgeT,{color:done?'#16A34A':inProg?accentColor:'#6B7280'}]}>
                {done?'Done':inProg?'In Progress':'Locked'}
              </Text>
            </View>
          </TouchableOpacity>
        );
      })}
      {d.modules.length===0&&(
        <View style={st.empty}><MaterialCommunityIcons name="book-outline" size={48} color="#CBD5E1"/><Text style={st.emptyT}>No modules available</Text></View>
      )}
    </View>
  );

  const renderSkills = () => (
    <View>
      <Text style={st.sectionTitle}>Skills You'll Gain</Text>
      <View style={{flexDirection:'row',flexWrap:'wrap',gap:10,marginBottom:20}}>
        {d.skills.map((sk,i)=>(
          <View key={i} style={[st.skillChip,{backgroundColor:accentColor+'18',borderColor:accentColor+'33'}]}>
            <MaterialCommunityIcons name="star-circle" size={14} color={accentColor}/>
            <Text style={[st.skillChipT,{color:accentColor}]}>{sk}</Text>
          </View>
        ))}
      </View>

      <Text style={st.sectionTitle}>Skill Match with Your Profile</Text>
      {d.skills.map((sk,i)=>{
        const hasSkill = (studentProfile.skills||[]).some(s=>s.toLowerCase().includes(sk.toLowerCase())||sk.toLowerCase().includes(s.toLowerCase()));
        return (
          <View key={i} style={[st.skillMatchRow,{borderLeftColor:hasSkill?'#10B981':'#E5E7EB'}]}>
            <Ionicons name={hasSkill?'checkmark-circle':'ellipse-outline'} size={18} color={hasSkill?'#10B981':'#CBD5E1'}/>
            <Text style={{flex:1,fontSize:14,color:'#374151',fontWeight:'500',marginLeft:10}}>{sk}</Text>
            <Text style={{fontSize:12,color:hasSkill?'#10B981':'#94A3B8',fontWeight:'600'}}>{hasSkill?'In your profile':'Learn this'}</Text>
          </View>
        );
      })}
    </View>
  );

  const renderApplication = () => (
    <View>
      <Text style={st.sectionTitle}>Application Process</Text>
      {d.applicationProcess.map((step,i)=>(
        <View key={i} style={st.processRow}>
          <View style={[st.stepNum,{backgroundColor:accentColor}]}><Text style={st.stepNumT}>{i+1}</Text></View>
          {i<d.applicationProcess.length-1&&<View style={[st.stepLine,{borderLeftColor:accentColor+'44'}]}/>}
          <Text style={st.stepT}>{step}</Text>
        </View>
      ))}

      {/* Status card if applied */}
      {isApplied&&(
        <View style={{backgroundColor:'#F0FDF4',borderRadius:12,padding:14,marginTop:16,borderWidth:1,borderColor:'#BBF7D0',flexDirection:'row',gap:10,alignItems:'center'}}>
          <Ionicons name="checkmark-circle" size={24} color="#10B981"/>
          <View style={{flex:1}}>
            <Text style={{fontSize:14,fontWeight:'700',color:'#065F46'}}>Application Submitted ✓</Text>
            <Text style={{fontSize:12,color:'#059669',marginTop:2}}>Check your Student Portal for status updates and recruiter responses.</Text>
          </View>
        </View>
      )}

      {/* Deadline warning */}
      {urgent&&!isPast&&(
        <View style={{backgroundColor:'#FEF2F2',borderRadius:12,padding:14,marginTop:12,borderWidth:1,borderColor:'#FECACA',flexDirection:'row',gap:10,alignItems:'center'}}>
          <Ionicons name="alarm" size={20} color="#EF4444"/>
          <Text style={{flex:1,fontSize:13,color:'#991B1B',fontWeight:'600'}}>⚠️ Only {days} day{days!==1?'s':''} left to apply!</Text>
        </View>
      )}
    </View>
  );

  const renderReviews = () => (
    <View>
      <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <View>
          <Text style={{fontSize:32,fontWeight:'900',color:'#1F2937'}}>{avgRating}</Text>
          <View style={{flexDirection:'row',gap:2,marginTop:2}}>{renderStars(parseFloat(avgRating))}</View>
          <Text style={{fontSize:12,color:'#64748B',marginTop:2}}>{reviews.length} reviews</Text>
        </View>
        <TouchableOpacity style={[st.applyBtn,{backgroundColor:accentColor,paddingHorizontal:16}]} onPress={()=>setShowReviewModal(true)}>
          <Text style={st.applyBtnT}>Write Review</Text>
        </TouchableOpacity>
      </View>

      {/* Rating breakdown */}
      {[5,4,3,2,1].map(s=>{
        const cnt = reviews.filter(r=>r.rating===s).length;
        return (
          <View key={s} style={{flexDirection:'row',alignItems:'center',gap:8,marginBottom:6}}>
            <Text style={{fontSize:12,color:'#64748B',width:12}}>{s}</Text>
            <MaterialCommunityIcons name="star" size={14} color="#F59E0B"/>
            <View style={{flex:1,height:7,backgroundColor:'#E5E7EB',borderRadius:4,overflow:'hidden'}}>
              <View style={{height:7,borderRadius:4,backgroundColor:'#F59E0B',width:`${reviews.length?Math.round(cnt/reviews.length*100):0}%`}}/>
            </View>
            <Text style={{fontSize:11,color:'#64748B',width:16,textAlign:'right'}}>{cnt}</Text>
          </View>
        );
      })}

      <View style={{height:14}}/>
      {reviews.map((rv,i)=>(
        <View key={i} style={st.reviewCard}>
          <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:6,alignItems:'center'}}>
            <View style={{flexDirection:'row',alignItems:'center',gap:8}}>
              <View style={{width:32,height:32,borderRadius:16,backgroundColor:accentColor,justifyContent:'center',alignItems:'center'}}>
                <Text style={{fontSize:13,fontWeight:'700',color:'#FFF'}}>{rv.name[0]}</Text>
              </View>
              <Text style={{fontSize:14,fontWeight:'600',color:'#1F2937'}}>{rv.name}</Text>
            </View>
            <View style={{flexDirection:'row',gap:2}}>{renderStars(rv.rating)}</View>
          </View>
          <Text style={{fontSize:13,color:'#374151',lineHeight:19}}>{rv.comment}</Text>
          <Text style={{fontSize:11,color:'#94A3B8',marginTop:6}}>{fmtDate(rv.date)}</Text>
        </View>
      ))}
    </View>
  );

  return (
    <SafeAreaView style={{flex:1,backgroundColor:'#F9FAFB'}}>
      <StatusBar barStyle="dark-content" backgroundColor="#F9FAFB"/>

      {/* Header */}
      <View style={st.header}>
        <TouchableOpacity style={st.hdrBtn} onPress={()=>navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color={accentColor}/>
        </TouchableOpacity>
        <Text style={st.hdrTitle}>Internship Details</Text>
        <View style={{flexDirection:'row',gap:6}}>
          <TouchableOpacity style={st.hdrBtn} onPress={handleSave}>
            <MaterialCommunityIcons name={isSaved?'bookmark':'bookmark-outline'} size={22} color={isSaved?accentColor:'#6B7280'}/>
          </TouchableOpacity>
          <TouchableOpacity style={st.hdrBtn} onPress={handleShare}>
            <MaterialCommunityIcons name="share-variant" size={22} color="#6B7280"/>
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView ref={scrollRef} showsVerticalScrollIndicator={false}>
        {/* Hero gradient */}
        <LinearGradient colors={[accentColor,accentColor+'99']} start={{x:0,y:0}} end={{x:1,y:1}} style={st.hero}>
          <View style={st.heroIcon}>
            <MaterialCommunityIcons name={d.icon||'briefcase'} size={44} color="#FFF"/>
          </View>
          <Text style={st.heroTitle}>{d.title}</Text>
          <Text style={st.heroCo}>{d.company}</Text>
          <View style={{flexDirection:'row',alignItems:'center',gap:6,marginTop:8}}>
            <View style={{flexDirection:'row',gap:2}}>{renderStars(parseFloat(avgRating))}</View>
            <Text style={{color:'rgba(255,255,255,0.9)',fontSize:13}}>{avgRating} ({d.participants?.toLocaleString()||0} participants)</Text>
          </View>
          {/* Status badges */}
          <View style={{flexDirection:'row',gap:8,marginTop:10,flexWrap:'wrap',justifyContent:'center'}}>
            {isApplied&&<View style={{backgroundColor:'rgba(255,255,255,0.25)',borderRadius:12,paddingHorizontal:12,paddingVertical:5}}><Text style={{color:'#FFF',fontSize:12,fontWeight:'700'}}>✓ Applied</Text></View>}
            {isSaved &&<View style={{backgroundColor:'rgba(255,255,255,0.25)',borderRadius:12,paddingHorizontal:12,paddingVertical:5}}><Text style={{color:'#FFF',fontSize:12,fontWeight:'700'}}>🔖 Saved</Text></View>}
            {urgent&&!isPast&&<View style={{backgroundColor:'#EF4444',borderRadius:12,paddingHorizontal:12,paddingVertical:5}}><Text style={{color:'#FFF',fontSize:12,fontWeight:'700'}}>{days}d left!</Text></View>}
          </View>
        </LinearGradient>

        {/* Match score if profile loaded */}
        {studentProfile.name&&d.matchScore&&(
          <Animated.View style={[st.matchRow,{opacity:fadeAnim}]}>
            <Ionicons name="trending-up-outline" size={18} color="#667eea"/>
            <Text style={{flex:1,fontSize:13,color:'#374151',fontWeight:'500'}}>
              <Text style={{fontWeight:'700',color:'#667eea'}}>{d.matchScore}% match</Text> with your profile, {studentProfile.name}
            </Text>
            <TouchableOpacity onPress={()=>setActiveTab('skills')}>
              <Text style={{fontSize:12,color:'#667eea',fontWeight:'600'}}>See details →</Text>
            </TouchableOpacity>
          </Animated.View>
        )}

        {/* Tab bar */}
        <View style={st.tabBar}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:8}}>
            {TABS.map(tab=>(
              <TouchableOpacity key={tab.id} style={[st.tab,activeTab===tab.id&&{borderBottomColor:accentColor,borderBottomWidth:3}]} onPress={()=>setActiveTab(tab.id)}>
                <MaterialCommunityIcons name={tab.icon} size={16} color={activeTab===tab.id?accentColor:'#6B7280'}/>
                <Text style={[st.tabT,activeTab===tab.id&&{color:accentColor,fontWeight:'700'}]}>{tab.title}</Text>
                {tab.id==='modules'&&progressPct>0&&progressPct<100&&(
                  <View style={{position:'absolute',top:6,right:6,width:8,height:8,borderRadius:4,backgroundColor:accentColor}}/>
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        <View style={{padding:16}}>
          {activeTab==='overview'    && renderOverview()}
          {activeTab==='modules'     && renderModules()}
          {activeTab==='skills'      && renderSkills()}
          {activeTab==='application' && renderApplication()}
          {activeTab==='reviews'     && renderReviews()}
        </View>
        <View style={{height:100}}/>
      </ScrollView>

      {/* Sticky footer */}
      <View style={st.footer}>
        <TouchableOpacity style={[st.footerSave,{borderColor:accentColor}]} onPress={handleSave}>
          <MaterialCommunityIcons name={isSaved?'bookmark':'bookmark-outline'} size={20} color={accentColor}/>
        </TouchableOpacity>
        <TouchableOpacity
          style={[st.footerApply,{backgroundColor:isPast?'#E5E7EB':isApplied?'#D1FAE5':accentColor},urgent&&!isPast&&!isApplied&&{backgroundColor:'#EF4444'}]}
          onPress={handleApply} disabled={isPast||applying}>
          {applying
            ? <ActivityIndicator size={18} color="#FFF"/>
            : <>
                <MaterialCommunityIcons name={isApplied?'check-circle':'send'} size={18} color={isPast||isApplied?'#6B7280':'#FFF'}/>
                <Text style={[st.footerApplyT,{color:isPast?'#6B7280':isApplied?'#059669':'#FFF'}]}>
                  {isPast?'Applications Closed':isApplied?'Applied ✓':urgent?'Apply Now!':'Apply for This Experience'}
                </Text>
              </>
          }
        </TouchableOpacity>
      </View>

      {/* ── Application Form Modal ── */}
      <Modal visible={showAppForm} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowAppForm(false)}>
        <KeyboardAvoidingView style={{flex:1,backgroundColor:'#FFF'}} behavior={Platform.OS==='ios'?'padding':'height'}>
          <View style={st.modalHdr}>
            <TouchableOpacity style={st.iconBtn} onPress={()=>setShowAppForm(false)}><Ionicons name="close" size={22} color="#6B7280"/></TouchableOpacity>
            <Text style={st.modalTitle}>Apply to {d.company}</Text>
            <TouchableOpacity style={[{backgroundColor:accentColor,borderRadius:10,paddingHorizontal:14,paddingVertical:8},applying&&{opacity:0.6}]} onPress={submitApplication} disabled={applying}>
              {applying?<ActivityIndicator size={13} color="#FFF"/>:<Text style={{color:'#FFF',fontWeight:'700',fontSize:13}}>Submit</Text>}
            </TouchableOpacity>
          </View>
          <ScrollView style={{flex:1,padding:16}} keyboardShouldPersistTaps="handled">
            <View style={{flexDirection:'row',backgroundColor:'#EEF2FF',borderRadius:12,padding:12,marginBottom:16,gap:10,alignItems:'center'}}>
              <View style={{width:40,height:40,borderRadius:10,backgroundColor:accentColor,justifyContent:'center',alignItems:'center'}}>
                <MaterialCommunityIcons name={d.icon||'briefcase'} size={20} color="#FFF"/>
              </View>
              <View><Text style={{fontSize:14,fontWeight:'700',color:'#1F2937'}}>{d.title}</Text><Text style={{fontSize:12,color:'#667eea',marginTop:1}}>{d.company} · {d.duration}</Text></View>
            </View>
            {studentProfile.name&&(
              <View style={{backgroundColor:'#F0FDF4',borderRadius:10,padding:10,marginBottom:14,flexDirection:'row',gap:8,alignItems:'center'}}>
                <Ionicons name="checkmark-circle" size={15} color="#10B981"/>
                <Text style={{flex:1,fontSize:12,color:'#065F46'}}>Applying as {studentProfile.name} · {studentProfile.university||''}. Your profile will be automatically attached.</Text>
              </View>
            )}
            {[
              {label:'Cover Letter *',key:'coverLetter',multi:true,ph:'Write a compelling cover letter…'},
              {label:'Why are you interested? *',key:'whyInterested',multi:true,ph:'Explain your motivation for this role…'},
              {label:'Relevant Experience',key:'relevantExperience',multi:true,ph:'Projects, coursework, previous internships…'},
              {label:'Availability *',key:'availability',multi:false,ph:'e.g. Available from July 2025, 12 weeks'},
            ].map(f=>(
              <View key={f.key} style={{marginBottom:14}}>
                <Text style={{fontSize:13,fontWeight:'600',color:'#374151',marginBottom:5}}>{f.label}</Text>
                <TextInput style={{borderWidth:1,borderColor:'#E5E7EB',borderRadius:10,paddingHorizontal:12,paddingVertical:11,fontSize:14,color:'#1F2937',backgroundColor:'#FAFAFA',...(f.multi?{minHeight:88,textAlignVertical:'top'}:{})}}
                  value={appForm[f.key]} multiline={f.multi} numberOfLines={f.multi?4:1}
                  onChangeText={v=>setAppForm(p=>({...p,[f.key]:v}))} placeholder={f.ph} placeholderTextColor="#9CA3AF"/>
              </View>
            ))}
            <View style={{height:24}}/>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>

      {/* ── Review Modal ── */}
      <Modal visible={showReviewModal} animationType="fade" transparent onRequestClose={()=>setShowReviewModal(false)}>
        <KeyboardAvoidingView style={{flex:1,backgroundColor:'rgba(0,0,0,0.5)',justifyContent:'center',padding:20}} behavior={Platform.OS==='ios'?'padding':'height'}>
          <View style={{backgroundColor:'#FFF',borderRadius:18,padding:20}}>
            <Text style={{fontSize:17,fontWeight:'800',color:'#1F2937',marginBottom:14}}>Write a Review</Text>
            <Text style={{fontSize:13,fontWeight:'600',color:'#374151',marginBottom:8}}>Rating</Text>
            <View style={{flexDirection:'row',gap:8,marginBottom:16}}>
              {[1,2,3,4,5].map(s=>(
                <TouchableOpacity key={s} onPress={()=>setReview(p=>({...p,rating:s}))}>
                  <MaterialCommunityIcons name={s<=review.rating?'star':'star-outline'} size={32} color="#F59E0B"/>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={{fontSize:13,fontWeight:'600',color:'#374151',marginBottom:6}}>Comment</Text>
            <TextInput style={{borderWidth:1,borderColor:'#E5E7EB',borderRadius:10,padding:12,fontSize:14,color:'#1F2937',minHeight:80,textAlignVertical:'top',marginBottom:16}} value={review.comment} onChangeText={v=>setReview(p=>({...p,comment:v}))} placeholder="Share your experience…" placeholderTextColor="#9CA3AF" multiline/>
            <View style={{flexDirection:'row',gap:10}}>
              <TouchableOpacity style={{flex:1,paddingVertical:12,borderRadius:10,backgroundColor:'#F1F5F9',alignItems:'center'}} onPress={()=>setShowReviewModal(false)}><Text style={{fontWeight:'700',color:'#374151'}}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={{flex:2,paddingVertical:12,borderRadius:10,backgroundColor:accentColor,alignItems:'center'}} onPress={submitReview}><Text style={{fontWeight:'700',color:'#FFF'}}>Submit</Text></TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  header:     {flexDirection:'row',alignItems:'center',justifyContent:'space-between',paddingHorizontal:14,paddingVertical:12,backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},
  hdrBtn:     {width:36,height:36,borderRadius:10,backgroundColor:'#F1F5F9',justifyContent:'center',alignItems:'center'},
  hdrTitle:   {fontSize:16,fontWeight:'700',color:'#374151'},
  iconBtn:    {width:36,height:36,borderRadius:10,backgroundColor:'#F1F5F9',justifyContent:'center',alignItems:'center'},
  hero:       {padding:24,alignItems:'center'},
  heroIcon:   {width:70,height:70,borderRadius:18,backgroundColor:'rgba(255,255,255,0.25)',justifyContent:'center',alignItems:'center',marginBottom:12},
  heroTitle:  {fontSize:20,fontWeight:'800',color:'#FFF',textAlign:'center',lineHeight:26},
  heroCo:     {fontSize:14,color:'rgba(255,255,255,0.85)',marginTop:4},
  matchRow:   {flexDirection:'row',alignItems:'center',backgroundColor:'#EEF2FF',marginHorizontal:14,marginTop:12,borderRadius:12,padding:12,gap:8},
  tabBar:     {backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB',marginTop:8},
  tab:        {flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:12,marginHorizontal:2,gap:5,borderBottomWidth:3,borderBottomColor:'transparent',position:'relative'},
  tabT:       {fontSize:12,color:'#6B7280',fontWeight:'500'},
  sectionTitle:{fontSize:15,fontWeight:'800',color:'#1F2937',marginBottom:10,marginTop:4},
  desc:       {fontSize:14,color:'#374151',lineHeight:22,marginBottom:16},
  infoGrid:   {flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:20},
  infoCard:   {flex:1,minWidth:140,backgroundColor:'#FFF',borderRadius:12,padding:12,borderWidth:1,alignItems:'center',gap:4},
  infoLabel:  {fontSize:11,color:'#94A3B8',fontWeight:'600',marginTop:2},
  infoValue:  {fontSize:13,fontWeight:'700',color:'#1F2937',textAlign:'center'},
  bulletRow:  {flexDirection:'row',alignItems:'flex-start',gap:8,marginBottom:8},
  bulletDot:  {width:7,height:7,borderRadius:4,marginTop:7},
  bulletT:    {flex:1,fontSize:14,color:'#374151',lineHeight:20},
  benefitChip:{flexDirection:'row',alignItems:'center',gap:5,borderRadius:12,borderWidth:1,paddingHorizontal:10,paddingVertical:5},
  benefitChipT:{fontSize:12,fontWeight:'600'},
  importantCard:{flexDirection:'row',gap:10,backgroundColor:'#FFF',borderRadius:12,padding:14,borderLeftWidth:4,marginTop:8,marginBottom:16,elevation:1,borderWidth:1,borderColor:'#E5E7EB'},
  progCard:   {backgroundColor:'#FFF',borderRadius:14,padding:14,marginBottom:14,borderWidth:1,borderColor:'#E5E7EB',elevation:1},
  modCard:    {flexDirection:'row',alignItems:'center',backgroundColor:'#FFF',borderRadius:12,padding:14,marginBottom:8,borderWidth:1,borderColor:'#E5E7EB',gap:12},
  modIcon:    {width:34,height:34,borderRadius:17,justifyContent:'center',alignItems:'center'},
  modTitle:   {fontSize:14,fontWeight:'600',color:'#1F2937'},
  modDur:     {fontSize:11,color:'#94A3B8',marginTop:2},
  modBadge:   {borderRadius:8,paddingHorizontal:8,paddingVertical:4},
  modBadgeT:  {fontSize:11,fontWeight:'600'},
  skillChip:  {flexDirection:'row',alignItems:'center',gap:5,borderRadius:12,borderWidth:1,paddingHorizontal:10,paddingVertical:6},
  skillChipT: {fontSize:12,fontWeight:'600'},
  skillMatchRow:{flexDirection:'row',alignItems:'center',paddingVertical:10,borderBottomWidth:1,borderBottomColor:'#F1F5F9',borderLeftWidth:3,paddingLeft:10,marginBottom:4},
  processRow: {flexDirection:'row',alignItems:'flex-start',gap:12,marginBottom:8,position:'relative'},
  stepNum:    {width:28,height:28,borderRadius:14,justifyContent:'center',alignItems:'center'},
  stepNumT:   {fontSize:13,fontWeight:'700',color:'#FFF'},
  stepLine:   {position:'absolute',left:14,top:28,bottom:-8,borderLeftWidth:2,borderLeftStyle:'dashed'},
  stepT:      {flex:1,fontSize:14,color:'#374151',paddingTop:4,lineHeight:20},
  reviewCard: {backgroundColor:'#F8FAFC',borderRadius:12,padding:14,marginBottom:10,borderWidth:1,borderColor:'#E5E7EB'},
  empty:      {alignItems:'center',paddingVertical:40},
  emptyT:     {fontSize:14,color:'#94A3B8',marginTop:10},
  footer:     {flexDirection:'row',gap:10,padding:14,backgroundColor:'#FFF',borderTopWidth:1,borderTopColor:'#E5E7EB',elevation:4},
  footerSave: {width:48,height:48,borderRadius:12,borderWidth:2,justifyContent:'center',alignItems:'center',backgroundColor:'#FFF'},
  footerApply:{flex:1,flexDirection:'row',justifyContent:'center',alignItems:'center',gap:8,paddingVertical:14,borderRadius:12},
  footerApplyT:{fontSize:15,fontWeight:'700'},
  applyBtn:   {backgroundColor:'#0e4e96',borderRadius:10,paddingHorizontal:14,paddingVertical:8,justifyContent:'center',alignItems:'center'},
  applyBtnT:  {fontSize:13,fontWeight:'700',color:'#FFF'},
  modalHdr:   {flexDirection:'row',alignItems:'center',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E5EA',gap:8},
  modalTitle: {flex:1,textAlign:'center',fontSize:16,fontWeight:'700',color:'#0F172A'},
});
